import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../Utils/firebase";
import { normalizeUserId } from "../Utils/chatUnread";

const readIsOnline = (data) => data?.isOnline === true;

/**
 * Real-time online status for direct-chat participants via Firestore users/{id}.isOnline
 */
export default function useDirectChatOnlineStatus(
  conversations,
  currentUserId,
) {
  const [onlineStatusMap, setOnlineStatusMap] = useState({});

  const directParticipantIds = useMemo(() => {
    const userId = normalizeUserId(currentUserId);
    if (!userId) return [];

    const ids = new Set();
    (conversations || []).forEach((item) => {
      if (item.type !== "direct") return;
      const otherId = item.participants?.find(
        (id) => normalizeUserId(id) !== userId,
      );
      if (otherId != null) {
        ids.add(normalizeUserId(otherId));
      }
    });

    return Array.from(ids).sort();
  }, [conversations, currentUserId]);

  useEffect(() => {
    if (directParticipantIds.length === 0) {
      setOnlineStatusMap({});
      return;
    }

    const unsubscribes = directParticipantIds.map((participantId) => {
      const userRef = doc(db, "users", participantId);

      return onSnapshot(
        userRef,
        (snap) => {
          const isOnline = snap.exists() ? readIsOnline(snap.data()) : false;
          setOnlineStatusMap((prev) => {
            if (prev[participantId] === isOnline) return prev;
            return { ...prev, [participantId]: isOnline };
          });
        },
        (error) => {
          console.error(
            `Error listening to online status for ${participantId}:`,
            error,
          );
        },
      );
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [directParticipantIds.join("|")]);

  return onlineStatusMap;
}
